/**
 * A party's own colour, made readable wherever it is printed.
 *
 * Party colours are stored on PoliticalParty.party_colors and are the real
 * brand hues - FDC's blue is #005BAA, NUP's red is #E4002B. Those are correct
 * as data and unreadable as 10px text on a near-black card: #005BAA against
 * black is a contrast ratio of about 1.7:1, well under the 4.5:1 that small
 * text needs.
 *
 * So the stored value stays true and the display value is lifted here, by
 * raising lightness while holding hue and saturation. A lifted #005BAA still
 * reads as FDC blue, where substituting a different blue outright would not.
 *
 * Lightness rather than a blend toward white: blending drains saturation as
 * it brightens, and red has to travel furthest to clear the threshold, so NUP
 * came out #ed5975 - visibly pink. Holding saturation keeps it red.
 */

/** Assumed backdrop: the card gradient is ~80% black behind the party line. */
const DARK_BACKDROP = '#262626';

/** WCAG AA for small text. */
const MIN_CONTRAST = 4.5;

const WHITE = '#FFFFFF';

type Rgb = { r: number; g: number; b: number };

function parseHex(hex: string): Rgb | null {
  const value = hex.trim().replace(/^#/, '');
  const full =
    value.length === 3
      ? value
          .split('')
          .map((c) => c + c)
          .join('')
      : value;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

function toHex({ r, g, b }: Rgb): string {
  const channel = (n: number) =>
    Math.round(Math.min(255, Math.max(0, n)))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

/** WCAG relative luminance. */
function luminance({ r, g, b }: Rgb): number {
  const channel = (n: number) => {
    const s = n / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function contrast(a: Rgb, b: Rgb): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

type Hsl = { h: number; s: number; l: number };

function toHsl({ r, g, b }: Rgb): Hsl {
  const [rn, gn, bn] = [r / 255, g / 255, b / 255];
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === rn) h = ((gn - bn) / d) % 6;
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  return { h: ((h * 60) % 360 + 360) % 360, s, l };
}

function fromHsl({ h, s, l }: Hsl): Rgb {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r1, g1, b1] =
    h < 60 ? [c, x, 0]
    : h < 120 ? [x, c, 0]
    : h < 180 ? [0, c, x]
    : h < 240 ? [0, x, c]
    : h < 300 ? [x, 0, c]
    : [c, 0, x];
  return { r: (r1 + m) * 255, g: (g1 + m) * 255, b: (b1 + m) * 255 };
}

/**
 * Lift `hex` until it reads against `backdrop`, keeping its hue.
 *
 * Steps lightness in fiftieths rather than solving directly: contrast is not
 * linear in lightness, and stepping both lands close to the threshold - so
 * the colour is no lighter than it has to be - and guarantees the loop ends.
 * A colour that is already legible is returned untouched.
 */
export function legibleOnDark(hex: string, backdrop: string = DARK_BACKDROP): string | null {
  const color = parseHex(hex);
  const ground = parseHex(backdrop);
  if (!color || !ground) return null;
  if (contrast(color, ground) >= MIN_CONTRAST) return toHex(color);

  const { h, s, l } = toHsl(color);
  const STEPS = 50;
  for (let step = 1; step <= STEPS; step += 1) {
    const candidate = fromHsl({ h, s, l: l + (1 - l) * (step / STEPS) });
    if (contrast(candidate, ground) >= MIN_CONTRAST) return toHex(candidate);
  }
  return WHITE;
}

export interface PartyColorSource {
  party_colors?: string[] | null;
}

/**
 * The colour to print a party's name in.
 *
 * Falls back to white, which is both the neutral answer for a party nobody
 * has recorded a colour for and the correct answer for an independent.
 */
export function partyTextColor(party?: PartyColorSource | null): string {
  const first = (party?.party_colors || []).find(
    (c) => typeof c === 'string' && c.trim() !== '',
  );
  if (!first) return WHITE;
  return legibleOnDark(first) ?? WHITE;
}
